import * as THREE from "three";
import type { EdgeSide } from "../level";
import type { StreetEnd } from "../maps";
import { WALL_SIDES } from "./beach";
import { mergeStatic } from "./merge";
import { inked, toon } from "./toon";

// A street end (#236, `look.streetEnds`): a stretch of the fence drawn as
// the line a city closes a street to cars with, and the street going on
// past it into town. Drawn only, like the beach: the sim keeps its wall
// there. Fixed layout, so every build looks the same; one mesh per material.

/** How far the street runs on past the barriers before a building closes the view (m). */
export const STREET_END_RUN = 40;
/** The sidewalk each side of the road, and how deep the buildings flanking it are (m). */
const SIDEWALK = 4;
export const STREET_END_FLANK = 10;
const FLANK = STREET_END_FLANK;

const FACADES = ["#b5836a", "#d6ccbb", "#8d8f93", "#9b5d45", "#c8b89c", "#6e7680"];
const AWNINGS = ["#2a9d8f", "#e76f51", "#264653", "#f4a261", "#8a5a83", "#3d7a4a"];

/**
 * Which stretch of wall each street end leaves out, along a side as its wall
 * is built (side k at angle k * 90 degrees, local x along it): the side's
 * other spans are what's left of the wall.
 */
export function localSpan(side: EdgeSide, from: number, to: number): [number, number] {
  // Local x along side k is world (cos a, -sin a) . (x, z): north runs along -x, east along -z, west along +z.
  const s = side === "south" || side === "west" ? 1 : -1;
  return s > 0 ? [from, to] : [-to, -from];
}

/** The pieces of a wall `len` long (centred on 0, local x) left once `cuts` are taken out of it. */
export function wallSpans(len: number, cuts: readonly [number, number][]): [number, number][] {
  let spans: [number, number][] = [[-len / 2, len / 2]];
  for (const [a, b] of cuts) {
    spans = spans.flatMap(([s, e]): [number, number][] => {
      if (b <= s || a >= e) return [[s, e]];
      return [...(a > s ? [[s, a] as [number, number]] : []), ...(b < e ? [[b, e] as [number, number]] : [])];
    });
  }
  return spans.filter(([s, e]) => e - s > 0.05);
}

/**
 * One street end, `fence` out from the middle: built with the street going
 * out along local +z (the south side's frame) and turned to its side.
 */
export function buildStreetEnd(end: StreetEnd, fence: number): THREE.Object3D {
  const root = new THREE.Group();
  root.rotation.y = (WALL_SIDES.indexOf(end.side) * Math.PI) / 2;
  const [x0, x1] = localSpan(end.side, end.from, end.to);
  const w = x1 - x0;
  const cx = (x0 + x1) / 2;
  const y = end.y;
  const add = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, py: number, z: number, cast = true) => {
    const m = new THREE.Mesh(geo, mat);
    m.position.set(x, py, z);
    m.castShadow = cast;
    m.receiveShadow = true;
    root.add(m);
    return m;
  };
  const paving = toon("#c9c1b4", { roughness: 0.9 }, "concrete");
  const asphalt = toon("#4d4d52", { roughness: 0.95 }, "concrete");
  const kerb = toon("#9d978e", { roughness: 0.9 }, "concrete");
  const paint = toon("#f2efe6", { roughness: 0.8 }, "paint");
  const barrier = toon("#d8d3c8", { roughness: 0.85 }, "concrete");
  const stripe = toon("#d6452f", { roughness: 0.7 }, "paint");
  const steel = toon("#4a5058", { roughness: 0.45, metalness: 0.5 }, "metal");
  const shrub = toon("#4f8a3c", { roughness: 0.9 }, "grass");
  const glass = toon("#35434d", { roughness: 0.15, metalness: 0.2, emissive: "#6b5a3a", emissiveIntensity: 0.25 }, "glass");
  const windows = toon("#9fb4c2", { roughness: 0.2, metalness: 0.3, emissive: "#3b4f5e", emissiveIntensity: 0.3 }, "glass");
  const bulb = new THREE.MeshBasicMaterial({ color: "#fff3cf" });

  // The street: its ground at the deck's height, out to where the last building closes it.
  const z0 = fence + 0.25;
  const run = STREET_END_RUN;
  if (y > 0) add(new THREE.BoxGeometry(w, y, run), kerb, cx, y / 2, z0 + run / 2, false);
  add(new THREE.BoxGeometry(w, 0.04, run), paving, cx, y + 0.02, z0 + run / 2, false);
  // A paved square past the barriers, then the road between kerbed sidewalks.
  const square = 6;
  const road = Math.max(3, w - SIDEWALK * 2);
  add(new THREE.BoxGeometry(road, 0.03, run - square), asphalt, cx, y + 0.035, z0 + square + (run - square) / 2, false);
  for (const s of [-1, 1]) add(new THREE.BoxGeometry(0.25, 0.15, run - square), kerb, cx + s * (road / 2 + 0.125), y + 0.075, z0 + square + (run - square) / 2);
  // A zebra crossing where the road meets the square, and a dashed centre line on up the road.
  for (let x = -road / 2 + 0.5; x < road / 2 - 0.4; x += 1.2) add(new THREE.BoxGeometry(0.6, 0.01, 3), paint, cx + x + 0.3, y + 0.055, z0 + square + 2, false);
  for (let z = square + 6; z < run - 2; z += 4) add(new THREE.BoxGeometry(0.15, 0.01, 2), paint, cx, y + 0.055, z0 + z, false);

  // The barrier line on the fence line: jersey barriers across the road, bollards across the sidewalks, a planter at each end.
  const line = z0 + 0.4;
  for (let x = -road / 2; x + 1.9 <= road / 2 + 0.01; x += 2) {
    const bx = cx + x + 0.95;
    add(new THREE.BoxGeometry(1.9, 0.35, 0.6), barrier, bx, y + 0.175, line);
    add(new THREE.BoxGeometry(1.9, 0.5, 0.3), barrier, bx, y + 0.6, line);
    add(new THREE.BoxGeometry(1.9, 0.08, 0.31), stripe, bx, y + 0.7, line, false);
  }
  for (const s of [-1, 1]) {
    const inner = road / 2 + 0.6;
    const outer = w / 2 - 1.6;
    for (let x = inner; x <= outer + 0.01; x += 1.4) {
      const bollard = inked(new THREE.CylinderGeometry(0.12, 0.14, 0.95, 10), steel, 0.01);
      bollard.position.set(cx + s * x, y + 0.475, line);
      bollard.castShadow = true;
      root.add(bollard);
    }
    const px = cx + s * (w / 2 - 0.8);
    add(new THREE.BoxGeometry(1.4, 0.8, 1.4), barrier, px, y + 0.4, line + 0.3);
    add(new THREE.SphereGeometry(0.75, 10, 8), shrub, px, y + 1.05, line + 0.3);
  }

  // Lamps along both sidewalks.
  for (const s of [-1, 1]) {
    for (let z = square + 3; z < run - 2; z += 10) {
      const x = cx + s * (w / 2 - 0.8);
      add(new THREE.CylinderGeometry(0.07, 0.1, 5, 8), steel, x, y + 2.5, z0 + z);
      add(new THREE.BoxGeometry(0.9, 0.12, 0.3), steel, x - s * 0.4, y + 5, z0 + z);
      add(new THREE.BoxGeometry(0.6, 0.02, 0.2), bulb, x - s * 0.5, y + 4.93, z0 + z, false);
    }
  }

  // Shopfronts flanking the street, standing up from the ground past each side, and one across its far end.
  let k = 0;
  // Each built facing -z (its front at -depth / 2), then turned: `facing` +1 faces +x, -1 faces -x, 0 back toward the park.
  const front = (x: number, z: number, along: number, facing: number, depth: number) => {
    const h = 9 + ((k * 5) % 8);
    const g = new THREE.Group();
    g.position.set(x, 0, z);
    g.rotation.y = (-facing * Math.PI) / 2;
    root.add(g);
    const part = (geo: THREE.BufferGeometry, mat: THREE.Material, py: number, pz: number, cast = false) => {
      const m = new THREE.Mesh(geo, mat);
      m.position.set(0, py, pz);
      m.castShadow = cast;
      m.receiveShadow = true;
      g.add(m);
    };
    const face = -depth / 2;
    part(new THREE.BoxGeometry(along, y + h, depth), toon(FACADES[k % FACADES.length]!, { roughness: 0.85 }, "concrete"), (y + h) / 2, 0);
    const gw = along * 0.8;
    part(new THREE.BoxGeometry(gw, 2.6, 0.12), glass, y + 1.5, face - 0.05);
    part(new THREE.BoxGeometry(gw, 0.12, 1.2), toon(AWNINGS[k % AWNINGS.length]!, { roughness: 0.7 }, "paint"), y + 3.05, face - 0.6, true);
    const floors = Math.floor((h - 4.5) / 3.2);
    for (let f = 0; f < floors; f++) part(new THREE.BoxGeometry(along * 0.78, 1.5, 0.12), windows, y + 5 + f * 3.2 + 0.75, face - 0.05);
    k++;
  };
  for (const s of [-1, 1]) {
    for (let z = 2; z + 8 <= run; z += 9) front(cx + s * (w / 2 + FLANK / 2), z0 + z + 4, 8.6, -s, FLANK);
  }
  front(cx, z0 + run + 5, w + FLANK * 2, 0, 10);

  return mergeStatic(root);
}
